import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, UserPlus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const when = (s) => (s ? new Date(s).toLocaleString() : "");
const KIND = { partner_review: "Partner decision", package_price: "Package price", partner_fees: "Partner fee rates" };

const Approval = ({ a, onDecide }) => {
  const [note, setNote] = useState("");
  return (
    <div data-testid={`approval-${a.id}`} className="lux-card p-5 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="sticker !text-[10px]">{KIND[a.kind] || a.kind}</span>
        <span className="text-xs text-slate-500">{a.requested_by_name} · Chief Operations Officer · {when(a.created_at)}</span>
      </div>
      <p data-testid={`approval-summary-${a.id}`} className="text-sm">{a.summary}</p>
      {a.note && <p className="text-xs text-slate-400">COO note: {a.note}</p>}
      {a.status === "pending" ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid={`approval-note-${a.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note back to the COO" className="lux-input flex-1" />
          <button data-testid={`approval-approve-${a.id}`} onClick={() => onDecide(a, "approve", note)} className="btn-gold px-4 py-2 text-sm inline-flex items-center gap-1"><Check className="w-4 h-4" />Approve</button>
          <button data-testid={`approval-decline-${a.id}`} onClick={() => onDecide(a, "decline", note)} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-1"><X className="w-4 h-4" />Decline</button>
        </div>
      ) : <p data-testid={`approval-status-${a.id}`} className="text-xs text-slate-400">{a.status} by {a.decided_by} · {when(a.decided_at)}{a.decision_note ? ` — ${a.decision_note}` : ""}</p>}
    </div>
  );
};

export const ApprovalsAdmin = () => {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get(`/admin/approvals?status=${status}`).then((r) => setItems(r.data)), [status]);
  useEffect(() => { load(); }, [load]);
  const decide = async (a, action, note) => {
    try { await api.post(`/admin/approvals/${a.id}/${action}`, { note }); toast.success(action === "approve" ? "Approved and applied" : "Declined"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="approvals-admin" className="space-y-4">
      <div className="flex gap-2">
        {["pending", "approved", "declined", "stale", "all"].map((s) => (
          <button key={s} data-testid={`approvals-filter-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs ${status === s ? "bg-[#38BDF8] text-[#0B0F19]" : "btn-ghost"}`}>{s}</button>
        ))}
      </div>
      {items.map((a) => <Approval key={a.id} a={a} onDecide={decide} />)}
      {!items.length && <p data-testid="approvals-empty" className="text-sm text-slate-500">No {status} requests.</p>}
    </div>
  );
};

export const TeamAdmin = () => {
  const [d, setD] = useState({ members: [], invites: [] });
  const [f, setF] = useState({ name: "", email: "" });
  const load = useCallback(() => api.get("/admin/team").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const invite = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/team/invite", f); toast.success(`Invite sent to ${f.email}`); setF({ name: "", email: "" }); load(); }
    catch (e2) { toast.error(formatApiError(e2)); }
  };
  const remove = async (kind, id) => {
    if (!window.confirm(kind === "member" ? "Remove this Chief Operations Officer's access?" : "Cancel this invite?")) return;
    try { await api.delete(`/admin/team/${kind}/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="team-admin" className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={invite} className="lux-card p-6 space-y-3">
        <p className="eyebrow flex items-center gap-2"><UserPlus className="w-4 h-4" />Invite a Chief Operations Officer</p>
        <p className="text-xs text-slate-500">They can approve partner packages, edit package details, and recommend partner decisions and pay-rate changes for your final approval. They can't see member details, payments, settings or schools.</p>
        <input data-testid="team-invite-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className="lux-input" />
        <input data-testid="team-invite-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className="lux-input" />
        <button data-testid="team-invite-btn" className="btn-gold px-5 py-2.5 text-sm">Send invite</button>
      </form>
      <div className="lux-card p-6 space-y-3">
        <p className="eyebrow">Team</p>
        {d.members.map((m) => (
          <div key={m.id} data-testid={`team-member-${m.id}`} className="flex items-center justify-between text-sm">
            <span>{m.name} <span className="text-slate-500">· {m.email} · {m.title}</span></span>
            <button data-testid={`team-remove-${m.id}`} onClick={() => remove("member", m.id)} className="text-rose-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {d.invites.map((i) => (
          <div key={i.id} data-testid={`team-invite-${i.id}`} className="flex items-center justify-between text-sm text-slate-400">
            <span>Invite pending: {i.name} · {i.email}</span>
            <button data-testid={`team-cancel-invite-${i.id}`} onClick={() => remove("invite", i.id)} className="text-rose-300"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {!d.members.length && !d.invites.length && <p className="text-sm text-slate-500" data-testid="team-empty">No team members yet.</p>}
      </div>
    </div>
  );
};
